import { Injectable } from '@angular/core';
import { Observable, scan, map, catchError, expand, EMPTY } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PaginationService {
/**
 * Recursively fetches all pages of data from a paginated API using a reactive stream.
 * @param fetchPageFn A function that fetches a single page of data and returns an Observable.
 * @returns An Observable that emits the full array of all items when the process is complete.
 */
  getAllPaginatedData<T>(
    fetchPageFn: (key: any) => Observable<{ Items: T[], lastEvaluatedKey: any }>
  ): Observable<T[]> {
    const allItems: T[] = [];
    let lastKey: any = null;

    return fetchPageFn(lastKey).pipe(
      // The expand operator will recursively call the next function
      expand(pageResult => {
        // Add a check to ensure pageResult exists before trying to access its properties.
        // We use optional chaining here.
        if (pageResult?.lastEvaluatedKey) {
          lastKey = pageResult.lastEvaluatedKey;
          return fetchPageFn(lastKey);
        } else {
          return EMPTY; // Completes the stream
        }
      }),
      // Map to accumulate all items from the 'Items' property
      map(pageResult => {
        // We use the nullish coalescing operator (??) here.
        // If pageResult or pageResult.Items is null or undefined, it defaults to an empty array []
        // This prevents the 'Cannot read properties of undefined' error
        allItems.push(...(pageResult?.Items ?? []));
        return allItems;
      })
    );
  }


  getAllPaginatedDataProgressively<T>(
    // Note the change here: no more `data` property.
    fetchPageFn: (key: any) => Observable<{ lastEvaluatedKey: any; items: T[] }>
  ): Observable<T[]> {
    return fetchPageFn(null).pipe(
      expand(pageResult => {
        if (pageResult && pageResult.lastEvaluatedKey) {
          return fetchPageFn(pageResult.lastEvaluatedKey);
        } else {
          return EMPTY;
        }
      }),
      // The scan operator is now looking for a `curr.items` array
      scan((acc, curr) => acc.concat(curr.items), [] as T[]),
      catchError(error => {
        console.error('Error fetching paginated data progressively:', error);
        return EMPTY;
      })
    );
  }


}
